import React, { useEffect, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import SeverityBadge from "@/components/shared/SeverityBadge";
import KPITile from "@/components/shared/KPITile";
import { Button } from "@/components/ui/button";
import api, { extractApiError } from "@/lib/api";
import { Check, X, Repeat, BedDouble, HeartPulse, Clock } from "lucide-react";
import { toast } from "sonner";
import { downloadReport } from "@/lib/downloadReport";
import { formatDisplayId } from "@/lib/utils";

export default function HospitalIncoming() {
  const [list, setList] = useState([]);
  const [beds, setBeds] = useState({ icu: { available: 0 }, general: { available: 0 } });
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setError("");
      try {
        const [patientsResponse, bedsResponse] = await Promise.all([
          api.get("/patients"),
          api.get("/beds"),
        ]);

        if (!mounted) return;

        const patientsData = Array.isArray(patientsResponse?.data)
          ? patientsResponse.data
          : Array.isArray(patientsResponse?.data?.data)
            ? patientsResponse.data.data
            : [];
        setList(patientsData.filter((patient) => String(patient?.status || "").toLowerCase() === "incoming"));

        const bedsData = bedsResponse?.data?.data || bedsResponse?.data || {};
        setBeds({
          icu: bedsData.icu || { available: 0 },
          general: bedsData.general || { available: 0 },
        });
      } catch (err) {
        if (!mounted) return;
        const { message } = extractApiError(err);
        setError(message || "Failed to load incoming patients.");
        setList([]);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  const accept = (id) => { setList(list.filter((x) => x.id !== id)); toast.success(`Patient ${id} accepted — bed assigned`); };
  const reject = (id) => { setList(list.filter((x) => x.id !== id)); toast.message(`Patient ${id} rejected`); };
  const redirect = (id) => { setList(list.filter((x) => x.id !== id)); toast.message(`Patient ${id} redirected to nearest hospital`); };

  return (
    <div className="space-y-6" data-testid="hospital-incoming">
      <PageHeader title="Incoming Patients" subtitle="Inbound patient queue with severity and ETA." onDownload={() => {
        downloadReport({
          filename: "hospital_incoming_patients",
          title: "Hospital - Incoming Patients Report",
          headers: ["ID", "Name", "Incident", "Severity", "Vitals", "Needs", "ETA"],
          rows: list.map((p) => [p.id, p.name, p.incident, p.severity, p.vitals, p.needs, p.eta]),
        });
        toast.success("Report downloaded");
      }} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPITile icon={HeartPulse} label="Inbound" value={list.length} accent="rose" />
        <KPITile icon={BedDouble} label="ICU available" value={beds.icu.available} accent="role" />
        <KPITile icon={BedDouble} label="General available" value={beds.general.available} accent="emerald" />
        <KPITile icon={Clock} label="Avg waiting" value="14 m" accent="amber" />
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <SectionCard padded={false}>
        <DataTable
          data={list}
          emptyState={
            <p className="px-5 py-12 text-center text-sm text-slate-500">No incoming patients</p>
          }
          getRowProps={(p) => ({ "data-testid": `patient-${p.id}` })}
          columns={[
            {
              key: "patient", label: "Patient",
              render: (p) => (
                <div>
                  <p className="font-semibold text-slate-900">{p.name}</p>
                  <p className="font-mono text-[10px] text-slate-400">{formatDisplayId(p.id)}</p>
                </div>
              ),
            },
            { key: "incident", label: "Incident", cellClassName: "font-mono text-xs" },
            { key: "severity", label: "Severity", render: (p) => <SeverityBadge severity={p.severity} /> },
            { key: "vitals", label: "Vitals", cellClassName: "text-xs text-slate-700" },
            { key: "needs", label: "Needs", cellClassName: "text-slate-700" },
            { key: "eta", label: "ETA", cellClassName: "font-semibold tabular-nums" },
            {
              key: "actions", label: "Actions", className: "text-right", cellClassName: "text-right",
              render: (p) => (
                <div className="flex justify-end gap-2">
                  <Button size="sm" className="bg-[hsl(var(--role-primary))] text-white hover:bg-[hsl(var(--role-primary))]/90" onClick={() => accept(p.id)} data-testid={`accept-${p.id}`}>
                    <Check size={13} className="mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => redirect(p.id)} data-testid={`redirect-${p.id}`}>
                    <Repeat size={13} className="mr-1" /> Redirect
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}>
                    <X size={13} />
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}
